import { Activity, Flame, Footprints, Scale, ArrowDown, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
export default function PhysicalCapacityPathway() {
  const nodes = [{
    category: "Aerobic Fitness",
    measure: "VO₂ Max",
    icon: Activity,
    label: "Aerobic Fitness / VO₂ Max"
  }, {
    category: "Metabolism",
    measure: "RMR",
    icon: Flame,
    label: "Metabolism / RMR"
  }, {
    category: "Movement",
    measure: "Gait and plantar pressure",
    icon: Footprints,
    label: "Movement / Gait and plantar pressure"
  }, {
    category: "Balance",
    measure: "Objective postural control measurement",
    icon: Scale,
    label: "Balance / Objective postural control measurement"
  }];
  return <section id="physicalcapacitypathway" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-6">
            A more complete picture of physical capacity
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
            No single measurement defines health or longevity. Combining complementary measures can provide a broader view of current physical capacity.
          </p>
        </div>

        {/* Pathway Diagram Container */}
        <div className="relative">
          {/* Top Row: 4 Assessment Nodes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 relative z-10">
            {nodes.map((node, i) => {
            const Icon = node.icon;
            return <div key={node.label} data-index={i} className="flex flex-col">
                  <Card className="flex-1 border-border shadow-xs hover:border-primary/40 transition-colors bg-card">
                    <CardContent className="p-6 flex flex-col justify-between h-full min-h-[160px]">
                      <div className="flex items-center justify-between mb-4">
                        <div className="size-10 rounded-lg bg-secondary text-primary flex items-center justify-center">
                          <Icon className="size-5" />
                        </div>
                        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                          0{i + 1}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs font-medium uppercase tracking-wider text-primary block mb-1">
                          {node.category}
                        </span>
                        <div className="text-base font-semibold text-card-foreground">
                          {node.measure}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>

          {/* Convergence Graphic / Connector Lines */}
          <div className="my-8 hidden lg:block" aria-hidden="true">
            <div className="relative h-16 w-full max-w-4xl mx-auto">
              <svg className="w-full h-full text-border" viewBox="0 0 800 64" fill="none" preserveAspectRatio="none">
                <path d="M100 0 V24 Q100 48 200 48 L400 48" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
                <path d="M300 0 V16 Q300 48 350 48 L400 48" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
                <path d="M500 0 V16 Q500 48 450 48 L400 48" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
                <path d="M700 0 V24 Q700 48 600 48 L400 48" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
                <path d="M400 48 V64" stroke="currentColor" strokeWidth="2" />
              </svg>
              <div className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-2 size-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-xs">
                <ArrowDown className="size-3.5" />
              </div>
            </div>
          </div>

          {/* Mobile/Tablet Fallback Connector */}
          <div className="flex lg:hidden justify-center my-6" aria-hidden="true">
            <div className="size-8 rounded-full bg-secondary text-primary flex items-center justify-center border border-border">
              <ArrowDown className="size-4" />
            </div>
          </div>

          {/* Outcome Convergence Node */}
          <div className="max-w-2xl mx-auto relative z-10">
            <Card className="border-border bg-card shadow-sm text-center">
              <CardContent className="p-8 sm:p-10 flex flex-col items-center">
                <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <CheckCircle2 className="size-6" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight max-w-lg leading-snug">
                  A broader understanding of your current fitness and movement capacity
                </h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}